import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * Static column schema for the audio-file-issues page filter / columns UI.
 *
 * This is the frontend analogue of {@link
 * file:tabasco/app/Services/AudioFileIssueFilterSchema.php}: the same logical
 * column ids (`code`, `track_name`, `artist_name`, `severity`, ...). The
 * search page generates its schema from `models.json` (one Eloquent model =
 * one searchable entity), but the audio-file-issues page filters fields that
 * span multiple tables (artist.name, album.name, original_status), so a
 * page-level schema is the simpler shape.
 *
 * Keys are persisted in user filter / column preferences (Pinia + localStorage);
 * renaming one invalidates saved state. Bump the synthetic entity id
 * (see {@link AUDIO_FILE_ISSUES_ENTITY_ID}) instead.
 */
export const AUDIO_FILE_ISSUES_ENTITY_ID = 'AudioFileIssue';

const baseStaticProps = {
  isFilled: false,
  isProhibited: false,
  isUnique: false,
} as const;

function staticColumn(
  id: string,
  header: string,
  dataType: 'string' | 'number' | 'Date' | 'display',
  options: { isNullable?: boolean; isSortable?: boolean; isFilterable?: boolean } = {},
): TableColumnStaticProps {
  return {
    id,
    key: id,
    field: id,
    header,
    dataType,
    isSortable: options.isSortable ?? true,
    isFilterable: options.isFilterable ?? true,
    isNullable: options.isNullable ?? false,
    ...baseStaticProps,
  };
}

/**
 * Display columns rendered on the table (each may be visible/hidden through the
 * columns dialog). The page renders custom cells for `cover_art`, `issue_type`,
 * `severity`, `details`, `verified_at`, `created_at`, `uploaded_date`,
 * `track_uploaded_date`, and `track_created_at` via the DataTable slot; the rest read straight from the
 * row attributes by key.
 */
export const AUDIO_FILE_ISSUE_COLUMNS: TableColumnStaticProps[] = [
  staticColumn('cover_art', 'Art', 'display', { isSortable: false, isFilterable: false }),
  staticColumn('code', 'Code', 'string'),
  staticColumn('track_name', 'Track', 'string'),
  staticColumn('artist_name', 'Artist', 'string'),
  staticColumn('album_name', 'Album', 'string', { isNullable: true }),
  staticColumn('genre_name', 'Genre', 'string', { isNullable: true }),
  staticColumn('record_label_name', 'Record Label', 'string', { isNullable: true }),
  staticColumn('isrc', 'ISRC', 'string', { isNullable: true }),
  staticColumn('created_at', 'Issue created', 'Date'),
  staticColumn('uploaded_date', 'File uploaded', 'Date', { isNullable: true }),
  staticColumn('track_uploaded_date', 'Track uploaded', 'Date', { isNullable: true }),
  staticColumn('track_created_at', 'Track created', 'Date'),
  staticColumn('copyright', 'Copyright', 'number', { isNullable: true }),
  staticColumn('bitrate', 'Bitrate', 'number', { isNullable: true }),
  staticColumn('duration', 'Duration', 'number', { isNullable: true }),
  staticColumn('playlist_count', 'Playlists', 'number', { isNullable: true }),
  // Maintained by the legacy system, and a separate fact from playlist_count —
  // a track can be in use without this app counting a playlist for it.
  staticColumn('is_in_use', 'Track Is In Use', 'number'),
  staticColumn('remix_name', 'Remix', 'string', { isNullable: true }),
  staticColumn('rating', 'Rating', 'string', { isNullable: true }),
  staticColumn('issue_type', 'Issue', 'string'),
  staticColumn('severity', 'Severity', 'string'),
  staticColumn('details', 'Details', 'string', { isSortable: false }),
  staticColumn('verified_at', 'Verified', 'Date'),
  staticColumn('original_status', 'Original backup', 'string', { isNullable: true }),
  staticColumn('original_bitrate_kbps', 'Original bitrate (kbps)', 'number', { isNullable: true }),
  staticColumn('gvlibrary_bitrate_kbps', 'Library bitrate (kbps)', 'number', { isNullable: true }),
];

/**
 * Columns visible on first load. Hidden columns can be revealed through the
 * Columns dialog. Matches the legacy fixed-column set so existing users see
 * the same layout when this ships.
 */
export const AUDIO_FILE_ISSUE_DEFAULT_VISIBLE: ReadonlySet<string> = new Set([
  'cover_art',
  'code',
  'track_name',
  'artist_name',
  'playlist_count',
  'is_in_use',
  'rating',
  'issue_type',
  'severity',
  'details',
  'verified_at',
]);

/** Default column widths in px. */
export const AUDIO_FILE_ISSUE_DEFAULT_WIDTHS: Record<string, number> = {
  cover_art: 56,
  code: 140,
  track_name: 240,
  artist_name: 200,
  album_name: 200,
  genre_name: 160,
  record_label_name: 200,
  isrc: 140,
  created_at: 140,
  uploaded_date: 140,
  track_uploaded_date: 140,
  track_created_at: 140,
  copyright: 100,
  bitrate: 100,
  duration: 100,
  playlist_count: 90,
  is_in_use: 130,
  remix_name: 200,
  rating: 90,
  issue_type: 220,
  severity: 110,
  details: 340,
  verified_at: 140,
  original_status: 140,
  original_bitrate_kbps: 140,
  gvlibrary_bitrate_kbps: 140,
};
